import { Button, Flex, FlexProps, Icon, Image, Text } from '@chakra-ui/react';
import { FaCoins } from 'react-icons/fa';
import { LuPackage } from 'react-icons/lu';

import type { SignalType } from '@features/create/CreateSignal';
import { Block, CommodityBlock, isCommodityBlock } from '@features/create/types';
import { ProcurementItem } from 'helpers/fakeData';
import { toPxString } from 'helpers/styles';

const PADDING_LEFT = 50;
const BACKET_OF_NEEDS_HEIGHT = 48;
const ITEM_HEIGHT = 72;

const CONFIRM_LABELS: Record<SignalType, string> = {
  demand: 'Confirm request',
};

const BASKET_LABELS: Record<SignalType, string> = {
  demand: 'Your new basket of needs',
};

interface Props {
  headerHeight: FlexProps['height'];
  blocks: Block[];
}

const SignalSummary = ({ headerHeight, blocks }: Props) => {
  const validBlocks = blocks
    .filter(isCommodityBlock)
    .filter((block): block is CommodityBlock & { commodity: ProcurementItem } => block.commodity != null);

  return (
    <Flex flexDirection="column" gap={4} h="full" pb={4}>
      <Flex
        height={headerHeight}
        alignItems="center"
        px={8}
        borderBottomColor="border.light"
        borderBottomWidth="1px"
        flexShrink={0}
      >
        <Text>Summary</Text>
      </Flex>
      <Flex pr={4}>
        <Flex width={toPxString(PADDING_LEFT)} alignItems="center" justifyContent="center" p={2}>
          <Icon boxSize="16px" color="text.secondary">
            <FaCoins />
          </Icon>
        </Flex>
        <Flex justifyContent="space-between" alignItems="center" w="full">
          <Flex flexDirection="column" gap={1}>
            <Text fontWeight="medium" fontSize="sm">
              Cash
            </Text>
            <Text fontSize="xs" color="text.secondary">
              Unallocated
            </Text>
          </Flex>
          <Text fontWeight="medium" fontSize="md">
            GHS10000
          </Text>
        </Flex>
      </Flex>
      <Flex flexDirection="column" overflowY="auto">
        <Flex flexDirection="column" position="relative" paddingLeft={toPxString(PADDING_LEFT)}>
          <TimelineSVG
            itemCount={validBlocks.length}
            itemHeight={ITEM_HEIGHT}
            headerHeight={BACKET_OF_NEEDS_HEIGHT}
            gutterWidth={PADDING_LEFT}
          />
          <Flex height={toPxString(BACKET_OF_NEEDS_HEIGHT)} alignItems="center">
            <Text fontWeight="medium" fontSize="sm">
              {BASKET_LABELS.demand}
            </Text>
          </Flex>
          {validBlocks.length === 0 && (
            <Flex alignItems="center">
              <Text color="text.secondary" fontSize="md" fontWeight="medium">
                Empty
              </Text>
            </Flex>
          )}
          {validBlocks.map((item) => (
            <Flex
              key={item.id}
              height={toPxString(ITEM_HEIGHT)}
              alignItems="center"
              justifyContent="space-between"
              p={2}
              pr={4}
              gap={2}
            >
              <Flex alignItems="center" gap={2} flex={1} minW={0}>
                {item.commodity.image != null ? (
                  <Image
                    src={item.commodity.image}
                    alt={item.commodity.name}
                    boxSize="48px"
                    flexShrink={0}
                  />
                ) : (
                  <Flex
                    boxSize="48px"
                    bg="surface.muted"
                    borderRadius="md"
                    alignItems="center"
                    justifyContent="center"
                    flexShrink={0}
                  >
                    <LuPackage size={22} />
                  </Flex>
                )}
                <Flex flexDirection="column" gap={1} fontWeight="medium" minW={0}>
                  <Text lineHeight={1} truncate>
                    {item.commodity.name}
                  </Text>
                  {item.commodity.ticker != null && (
                    <Text color="text.secondary" lineHeight={1} fontSize="sm">
                      {item.commodity.ticker}
                    </Text>
                  )}
                </Flex>
              </Flex>
              <Flex flexDirection="column" alignItems="flex-end" flexShrink={0}>
                <Text fontWeight="medium" fontSize="xs" color="text.secondary" whiteSpace="nowrap">
                  Deposit required
                </Text>
                <Text fontSize="md" fontWeight="medium">
                  GHS100
                </Text>
              </Flex>
            </Flex>
          ))}
        </Flex>
      </Flex>
      <Button mx={6} disabled={validBlocks.length === 0} size="sm">
        {CONFIRM_LABELS.demand}
      </Button>
      <Button mx={6} size="sm" variant="secondaryOutline">
        Reset
      </Button>
    </Flex>
  );
};

type TimelineProps = {
  itemCount: number;
  itemHeight: number;
  headerHeight: number;
  gutterWidth: number;
  strokeColor?: string;
};

const TimelineSVG = ({
  itemCount,
  itemHeight,
  headerHeight,
  gutterWidth,
  strokeColor = 'var(--chakra-colors-border-light)',
}: TimelineProps) => {
  const x = gutterWidth / 2;
  const totalHeight = headerHeight + itemCount * itemHeight;
  const dotY = headerHeight / 2;

  const lastItemY = headerHeight + (itemCount - 1) * itemHeight + itemHeight / 2;

  // Trunk: one continuous vertical line from the dot down to the last curve's departure
  let d = `M ${x} ${dotY} L ${x} ${lastItemY - x}`;

  // Branches: separate curve subpaths forking off the trunk at each item
  for (let i = 0; i < itemCount; i++) {
    const itemY = headerHeight + i * itemHeight + itemHeight / 2;
    d += ` M ${x} ${itemY - x} Q ${x} ${itemY}, ${gutterWidth} ${itemY}`;
  }

  return (
    <svg width={gutterWidth} height={totalHeight} style={{ position: 'absolute', left: 0, top: 0 }}>
      <path d={d} fill="none" stroke={strokeColor} strokeWidth={2} />
      <circle cx={x} cy={dotY} r={5} fill={strokeColor} />
    </svg>
  );
}

export default SignalSummary;
